import React from 'react';
import styled from '../../styled';

/**
 * Static "daylight horizon" hero background.
 *
 * Sky + horizon arc live in one layer so the arc stays visible at the
 * bottom of the content box. Splitting them caused the rim to clip when
 * the arc center was pushed below the content area.
 */
const GradientBg = styled('div')`
  position: absolute;
  inset: 0;
  z-index: 0;
  pointer-events: none;

  background:
    radial-gradient(
        125% 90% at 50% 176%,
        rgba(46, 92, 255, 0) 97.6%,
        rgba(46, 92, 255, 0.32) 98.6%,
        rgba(46, 92, 255, 0) 100%
      ),
    radial-gradient(
        125% 90% at 50% 176%,
        #ffffff 0%,
        #ffffff 90%,
        rgba(255, 255, 255, 0.9) 96%,
        rgba(255, 255, 255, 0) 99.5%
      ),
    radial-gradient(
        125% 90% at 50% 176%,
        rgba(46, 92, 255, 0) 97%,
        rgba(46, 92, 255, 0.16) 101%,
        rgba(127, 163, 255, 0.1) 108%,
        rgba(46, 92, 255, 0) 122%
      ),
    linear-gradient(180deg, #dbe7fa 0%, #e9f1fd 52%, #f6f9ff 100%);
`;

const GradientRoot = styled('div')`
  position: absolute;
  inset: 0;
  z-index: 0;
  pointer-events: none;
  overflow: hidden;
`;

/**
 * Soft multi-hue blobs layered over the existing sky/arc — not a
 * replacement mesh. Kept in the upper sky so they don't meet the horizon.
 */
const HorizonColorBlobs = styled('div')`
  position: absolute;
  inset: 0;
  background:
    radial-gradient(
      ellipse 28% 45% at 14% 18%,
      rgba(120, 190, 255, 0.32) 0%,
      rgba(120, 190, 255, 0) 70%
    ),
    radial-gradient(
      ellipse 32% 50% at 78% 10%,
      rgba(160, 140, 255, 0.24) 0%,
      rgba(160, 140, 255, 0) 68%
    ),
    radial-gradient(
      ellipse 30% 48% at 94% 28%,
      rgba(46, 92, 255, 0.22) 0%,
      rgba(46, 92, 255, 0) 66%
    ),
    radial-gradient(
      ellipse 24% 42% at 38% 8%,
      rgba(190, 210, 255, 0.26) 0%,
      rgba(190, 210, 255, 0) 70%
    ),
    radial-gradient(
      ellipse 26% 40% at 58% 20%,
      rgba(90, 210, 230, 0.16) 0%,
      rgba(90, 210, 230, 0) 65%
    );
`;

/** Horizon sky + arc + color blobs (use-cases, product, pricing, mcp, etc.). */
export const GradientBackground = () => (
  <GradientRoot aria-hidden="true">
    <GradientBg />
    <HorizonColorBlobs />
  </GradientRoot>
);

/* ------------------------------------------------------------------ */
/* Homepage hero variant: horizon base + color blobs + sun rays.       */
/* ------------------------------------------------------------------ */

/** Soft glow where the "sun" sits, just outside the top-left corner. */
const SunGlow = styled('div')`
  position: absolute;
  inset: 0;
  background: radial-gradient(
    circle 620px at -4% -12%,
    rgba(255, 255, 255, 0.5) 0%,
    rgba(255, 253, 245, 0.22) 34%,
    rgba(255, 255, 255, 0) 68%
  );
`;

/**
 * Crepuscular rays: a conic gradient anchored at the sun point just
 * outside the top-left corner, so the beams genuinely radiate from it.
 * A radial mask (same anchor) fades them with distance, and a blur
 * keeps every edge soft.
 *
 * The layer is only slightly larger than the hero (not a giant square)
 * to keep the blurred rasterized area cheap; the sway rotation pivots
 * around the sun point and the -10% inset hides rotation gaps.
 *
 * Conic angles: 0deg points up, clockwise. From the top-left corner the
 * beams aimed into the hero fall between ~92deg (right) and ~174deg
 * (down). The sky is nearly white, so beams alternate bright white with
 * faintly deeper sky blue — the contrast between them reads as rays.
 */
const SUN_POINT = '2% -6%';

const SunRays = styled('div')`
  position: absolute;
  inset: -10%;

  background: conic-gradient(
    from 0deg at ${SUN_POINT},
    rgba(126, 160, 235, 0) 92deg,
    rgba(255, 255, 255, 0.58) 101deg,
    rgba(126, 160, 235, 0.2) 111deg,
    rgba(255, 255, 255, 0.42) 121deg,
    rgba(126, 160, 235, 0.22) 132deg,
    rgba(255, 255, 255, 0.52) 143deg,
    rgba(126, 160, 235, 0.18) 154deg,
    rgba(255, 255, 255, 0.34) 163deg,
    rgba(126, 160, 235, 0) 174deg,
    rgba(126, 160, 235, 0) 360deg
  );

  mask-image: radial-gradient(
    circle farthest-side at ${SUN_POINT},
    rgba(0, 0, 0, 0.85) 0%,
    rgba(0, 0, 0, 0.4) 45%,
    rgba(0, 0, 0, 0) 78%
  );
  -webkit-mask-image: radial-gradient(
    circle farthest-side at ${SUN_POINT},
    rgba(0, 0, 0, 0.85) 0%,
    rgba(0, 0, 0, 0.4) 45%,
    rgba(0, 0, 0, 0) 78%
  );

  filter: blur(24px);
  transform-origin: ${SUN_POINT};
  will-change: transform, opacity;

  /* ±4.5deg over 10s: at ~1200px from the sun point the ray tips sweep
     ~90px per half-cycle — clearly alive without feeling animated. */
  @keyframes heroRaysSway {
    0% {
      transform: rotate(-4.5deg);
      opacity: 0.65;
    }
    50% {
      opacity: 1;
    }
    100% {
      transform: rotate(4.5deg);
      opacity: 0.7;
    }
  }

  animation: heroRaysSway 10s ease-in-out infinite alternate;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
    will-change: auto;
    opacity: 0.8;
  }
`;

export const HeroGradientBackground = () => (
  <GradientRoot aria-hidden="true">
    <GradientBg />
    <HorizonColorBlobs />
    <SunGlow />
    <SunRays />
  </GradientRoot>
);
